import {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
  useRef,
  useEffect
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { Product as ProductDTO } from '@setvi/shared/interfaces';
import { QuoteDTO, QuoteItemDTO } from '@setvi/shared/services/react-query';
import { getQuoteStatusesQuery } from 'Services';
import { QuotesStatus } from 'Services/Query/Quotes/Types';
import { Moment } from 'moment';
import {
  useQuote,
  useAddProducts,
  useRemoveQuoteItem,
  useAddSection,
  useAddSectionAndMoveItem,
  useUpdateSection,
  useUpdateQuoteStatus,
  useRemoveSectionKeepItems,
  useRefreshPrices,
  useMoveItem,
  useMoveItems,
  useRefreshSinglePrice,
  useReorderItems,
  useDebouncedItemUpdate,
  useRenewQuote,
  useQuoteCache
} from '../hooks';

// =============================================================================
// Context Types - Split into focused interfaces to enable granular subscriptions
// =============================================================================

/** Quote data that changes frequently */
interface QuoteDataContextValue {
  quoteId: string;
  quote: QuoteDTO | undefined;
  isLoading: boolean;
  error: any;
  statuses: QuotesStatus[];
  currentStatus: QuotesStatus | null;
  isLoadingStatuses: boolean;
}

/** Stable mutation functions - rarely change */
interface QuoteMutationsContextValue {
  updateQuote: (updates: Partial<QuoteDTO>) => void;
  addProducts: (params: {
    products: ProductDTO[];
    parentId?: string | null;
  }) => Promise<any>;
  removeItem: (itemId: string) => void;
  moveItem: (itemId: string, targetSectionId: string | null) => void;
  moveItems: (itemIds: string[], targetSectionId: string | null) => void;
  addSection: (params: { name: string; description?: string }) => void;
  addSectionAndMoveItem: (params: {
    name: string;
    description?: string;
    itemId: string;
  }) => void;
  updateSection: (params: {
    sectionId: string;
    name: string;
    description?: string;
  }) => void;
  removeSection: (sectionId: string) => void;
  removeSectionKeepItems: (sectionId: string) => void;
  refreshPrices: () => void;
  refreshSinglePrice: (params: { productId: string; sku: string }) => void;
  reorderItems: (reorderedItems: QuoteItemDTO[]) => Promise<any>;
  updateQuoteStatus: (statusId: string) => void;
  renewQuote: (params: { validUntil: Date | Moment }) => Promise<any>;
  updateItem: {
    mutate: (params: {
      itemId: string;
      updates: {
        quantity?: number;
        sell?: number;
        note?: string;
        labelType?: string;
        customLabel?: string;
        includeInTotalCost?: boolean;
      };
    }) => void;
    cancel: () => void;
  };
}

/** Loading/status flags - change during mutations */
interface QuoteStatusContextValue {
  isSaving: boolean;
  hasSaveError: boolean;
  isAddingProducts: boolean;
  isRefreshingPrices: boolean;
  isRefreshingSinglePrice: boolean;
  isReorderingItems: boolean;
  isUpdatingQuoteStatus: boolean;
  isRenewingQuote: boolean;
}

/** Combined context for backwards compatibility */
type QuoteContextValue = QuoteDataContextValue &
  QuoteMutationsContextValue &
  QuoteStatusContextValue;

// =============================================================================
// Contexts - Separate contexts enable granular subscriptions
// =============================================================================

export const QuoteDataContext = createContext<QuoteDataContextValue | null>(
  null
);
export const QuoteMutationsContext =
  createContext<QuoteMutationsContextValue | null>(null);
export const QuoteStatusContext = createContext<QuoteStatusContextValue | null>(
  null
);

/** @deprecated Use useQuoteData, useQuoteMutations, or useQuoteStatus instead */
export const QuoteContext = createContext<QuoteContextValue | null>(null);

export interface QuoteProviderProps {
  children: ReactNode;
  quoteId: string;
}

/**
 * Quote Context Provider
 * Thin wrapper over React Query hooks that provides quote data and mutations
 * Eliminates prop drilling while still leveraging React Query's caching and optimistic updates
 */
export const QuoteProvider = ({ children, quoteId }: QuoteProviderProps) => {
  // Fetch quote data
  const { data: quote, isLoading, error } = useQuote(quoteId);

  // Cache operations
  const cache = useQuoteCache(quoteId);

  // Update quote in cache (memoized to prevent unnecessary re-renders)
  const updateQuote = useCallback(
    (updates: Partial<QuoteDTO>) => {
      cache.setQuote(old => (old ? { ...old, ...updates } : old));
    },
    [cache]
  );

  // Fetch quote statuses
  const { data: statuses = [], isFetching: isLoadingStatuses } = useQuery(
    getQuoteStatusesQuery()
  );

  // Get current status object
  const currentStatus = useMemo(
    () => statuses.find(s => s._id === quote?.statusId) || null,
    [statuses, quote?.statusId]
  );

  // Immediate save mutations
  const addProductsMutation = useAddProducts(quoteId);
  const removeQuoteItemMutation = useRemoveQuoteItem(quoteId);
  const moveItemMutation = useMoveItem(quoteId);
  const moveItemsMutation = useMoveItems(quoteId);
  const addSectionMutation = useAddSection(quoteId);
  const addSectionAndMoveItemMutation = useAddSectionAndMoveItem(quoteId);
  const updateSectionMutation = useUpdateSection(quoteId);
  const updateQuoteStatusMutation = useUpdateQuoteStatus(quoteId);
  const removeSectionKeepItemsMutation = useRemoveSectionKeepItems(quoteId);
  const refreshPricesMutation = useRefreshPrices(quoteId);
  const refreshSinglePriceMutation = useRefreshSinglePrice(quoteId);
  const reorderItemsMutation = useReorderItems(quoteId);
  const renewQuoteMutation = useRenewQuote(quoteId);

  // Debounced item update - applies changes immediately, debounces API call
  const debouncedItemUpdate = useDebouncedItemUpdate(quoteId, 1500);

  // Refs to track pending refresh requests (to coordinate with debounced updates)
  const pendingRefreshPricesRef = useRef(false);
  const pendingRefreshSinglePriceRef = useRef<{
    productId: string;
    sku: string;
  } | null>(null);

  // Effect to trigger pending refreshes when debounced update completes
  useEffect(() => {
    if (!debouncedItemUpdate.isLoading) {
      // Wait 1000ms before triggering pending refreshes to allow for rapid edits
      const timeoutId = setTimeout(() => {
        if (pendingRefreshPricesRef.current) {
          pendingRefreshPricesRef.current = false;
          refreshPricesMutation.mutate();
        }
        if (pendingRefreshSinglePriceRef.current) {
          const params = pendingRefreshSinglePriceRef.current;
          pendingRefreshSinglePriceRef.current = null;
          refreshSinglePriceMutation.mutate(params);
        }
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [
    debouncedItemUpdate.isLoading,
    refreshPricesMutation,
    refreshSinglePriceMutation
  ]);

  // Derived saving status flags (passed to SavingStatus component)
  const isSaving =
    addProductsMutation.isLoading ||
    removeQuoteItemMutation.isLoading ||
    moveItemMutation.isLoading ||
    moveItemsMutation.isLoading ||
    addSectionMutation.isLoading ||
    addSectionAndMoveItemMutation.isLoading ||
    updateSectionMutation.isLoading ||
    updateQuoteStatusMutation.isLoading ||
    removeSectionKeepItemsMutation.isLoading ||
    reorderItemsMutation.isLoading ||
    debouncedItemUpdate.isLoading;

  const hasSaveError =
    addProductsMutation.isError ||
    removeQuoteItemMutation.isError ||
    moveItemMutation.isError ||
    moveItemsMutation.isError ||
    addSectionMutation.isError ||
    addSectionAndMoveItemMutation.isError ||
    updateSectionMutation.isError ||
    updateQuoteStatusMutation.isError ||
    removeSectionKeepItemsMutation.isError ||
    reorderItemsMutation.isError ||
    !!debouncedItemUpdate.error;

  // Wrapper functions for simpler API (memoized to prevent unnecessary re-renders)
  const removeItem = useCallback(
    (itemId: string) =>
      removeQuoteItemMutation.mutate({ itemId, itemType: 'product' }),
    [removeQuoteItemMutation]
  );

  const removeSection = useCallback(
    (sectionId: string) =>
      removeQuoteItemMutation.mutate({
        itemId: sectionId,
        itemType: 'section'
      }),
    [removeQuoteItemMutation]
  );

  const moveItem = useCallback(
    (itemId: string, targetSectionId: string | null) =>
      moveItemMutation.mutate({ itemId, targetSectionId }),
    [moveItemMutation]
  );

  const moveItems = useCallback(
    (itemIds: string[], targetSectionId: string | null) =>
      moveItemsMutation.mutate({ itemIds, targetSectionId }),
    [moveItemsMutation]
  );

  const updateQuoteStatus = useCallback(
    (statusId: string) => updateQuoteStatusMutation.mutate({ statusId }),
    [updateQuoteStatusMutation]
  );

  const reorderItems = useCallback(
    (reorderedItems: QuoteItemDTO[]) =>
      reorderItemsMutation.mutateAsync({ reorderedItems }),
    [reorderItemsMutation]
  );

  // Wrapper for refreshPrices that waits for pending updateItem to complete
  const refreshPrices = useCallback(() => {
    if (debouncedItemUpdate.isLoading) {
      // Queue the refresh to trigger after updateItem completes
      pendingRefreshPricesRef.current = true;
    } else {
      refreshPricesMutation.mutate();
    }
  }, [debouncedItemUpdate.isLoading, refreshPricesMutation]);

  // Wrapper for refreshSinglePrice that waits for pending updateItem to complete
  const refreshSinglePrice = useCallback(
    (params: { productId: string; sku: string }) => {
      if (debouncedItemUpdate.isLoading) {
        // Queue the refresh to trigger after updateItem completes
        pendingRefreshSinglePriceRef.current = params;
      } else {
        refreshSinglePriceMutation.mutate(params);
      }
    },
    [debouncedItemUpdate.isLoading, refreshSinglePriceMutation]
  );

  // =============================================================================
  // Split context values - each memoized separately for granular re-renders
  // =============================================================================

  // Data context - changes when quote data changes
  const dataValue: QuoteDataContextValue = useMemo(
    () => ({
      quoteId,
      quote,
      isLoading,
      error,
      statuses,
      currentStatus,
      isLoadingStatuses
    }),
    [
      quoteId,
      quote,
      isLoading,
      error,
      statuses,
      currentStatus,
      isLoadingStatuses
    ]
  );

  // Mutations context - stable functions, rarely changes
  const mutationsValue: QuoteMutationsContextValue = useMemo(
    () => ({
      updateQuote,
      addProducts: addProductsMutation.mutateAsync,
      removeItem,
      moveItem,
      moveItems,
      addSection: addSectionMutation.mutate,
      addSectionAndMoveItem: addSectionAndMoveItemMutation.mutate,
      updateSection: updateSectionMutation.mutate,
      removeSection,
      removeSectionKeepItems: removeSectionKeepItemsMutation.mutate,
      refreshPrices,
      refreshSinglePrice,
      reorderItems,
      updateQuoteStatus,
      renewQuote: renewQuoteMutation.mutateAsync,
      updateItem: debouncedItemUpdate
    }),
    [
      updateQuote,
      addProductsMutation.mutateAsync,
      removeItem,
      moveItem,
      moveItems,
      addSectionMutation.mutate,
      addSectionAndMoveItemMutation.mutate,
      updateSectionMutation.mutate,
      removeSection,
      removeSectionKeepItemsMutation.mutate,
      refreshPrices,
      refreshSinglePrice,
      reorderItems,
      updateQuoteStatus,
      renewQuoteMutation.mutateAsync,
      debouncedItemUpdate
    ]
  );

  // Status context - changes during mutations
  const statusValue: QuoteStatusContextValue = useMemo(
    () => ({
      isSaving,
      hasSaveError,
      isAddingProducts: addProductsMutation.isLoading,
      isRefreshingPrices: refreshPricesMutation.isLoading,
      isRefreshingSinglePrice: refreshSinglePriceMutation.isLoading,
      isReorderingItems: reorderItemsMutation.isLoading,
      isUpdatingQuoteStatus: updateQuoteStatusMutation.isLoading,
      isRenewingQuote: renewQuoteMutation.isLoading
    }),
    [
      isSaving,
      hasSaveError,
      addProductsMutation.isLoading,
      refreshPricesMutation.isLoading,
      refreshSinglePriceMutation.isLoading,
      reorderItemsMutation.isLoading,
      updateQuoteStatusMutation.isLoading,
      renewQuoteMutation.isLoading
    ]
  );

  return (
    <QuoteDataContext.Provider value={dataValue}>
      <QuoteMutationsContext.Provider value={mutationsValue}>
        <QuoteStatusContext.Provider value={statusValue}>
          {children}
        </QuoteStatusContext.Provider>
      </QuoteMutationsContext.Provider>
    </QuoteDataContext.Provider>
  );
};

// =============================================================================
// Hooks - Use specific hooks for better performance
// =============================================================================

/**
 * Hook to access quote data (quote, items, statuses)
 * Use this when you only need to read quote data
 * Components using this will re-render when quote data changes
 */
export const useQuoteData = () => {
  const context = useContext(QuoteDataContext);

  if (!context) {
    throw new Error('useQuoteData must be used within QuoteProvider');
  }

  return context;
};

/**
 * Hook to access mutation functions
 * Use this when you only need to perform mutations
 * Components using this will NOT re-render when quote data changes
 */
export const useQuoteMutations = () => {
  const context = useContext(QuoteMutationsContext);

  if (!context) {
    throw new Error('useQuoteMutations must be used within QuoteProvider');
  }

  return context;
};

/**
 * Hook to access loading/status flags
 * Use this when you need to show loading or error states
 */
export const useQuoteStatusFlags = () => {
  const context = useContext(QuoteStatusContext);

  if (!context) {
    throw new Error('useQuoteStatusFlags must be used within QuoteProvider');
  }

  return context;
};
